import "../TransactionPanelShell.css";

import { ReactElement } from "react";
import { createPortal } from "react-dom";

import {
  CollaboratorTypeConstants,
  InteractionObjectType,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../../../constants";
import { UploadDocumentModal } from "../../../../route/shared/projects/project/documents/components/UploadDocumentModal";
import { GetProjectIssuancesResponse } from "../../../../service/query";
import { formatNumber } from "../../../../utils";
import {
  ActionButtons,
  Column,
  DatePicker,
  ErrorBox,
  FlyoutPanel,
  FormLabel,
  InformationBox,
  PillsList,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../../../widget";
import { isUnitKmHabitatType } from "../../../IssuancePanel";
import { TagsPanel } from "../../../TagsPanel";
import { QuoteExpiryField } from "../QuoteExpiryField";
import { TransactionAttachments } from "../TransactionAttachments";
import { TransactionCompanyPicker } from "../TransactionCompanyPicker";
import { useAddIssuanceTransactionPanel } from "./useAddIssuanceTransactionPanel";

interface AddIssuanceTransactionPanelProps {
  issuance?: GetProjectIssuancesResponse["results"][0];
  variant?: "hub" | "seed";
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

export const AddIssuanceTransactionPanel = ({
  issuance,
  variant = "seed",
  show,
  onClose,
  handleRefresh,
}: AddIssuanceTransactionPanelProps): JSX.Element => {
  const {
    errors,
    handleClosePanel,
    handleSubmit,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    showTagsPanel,
    setShowTagsPanel,
    changedTagsExternal,
    setChangedTagsExternal,
    setRefreshTags,
    tagPills,
    documents,
    canManageAttachments,
    projectUuid,
    showUploadDocumentModal,
    handleAddAttachment,
    handleCloseUploadDocumentModal,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
  } = useAddIssuanceTransactionPanel({
    show,
    issuance,
    onClose,
    handleRefresh,
  });

  const corners = variant === "seed" ? "rounded" : "default";
  const font = variant === "seed" ? "figtree" : "default";

  const content = (): ReactElement => {
    return (
      <form className="TransactionPanel_form" onSubmit={(e) => handleSubmit(e)}>
        {errors && errors.length > 0 && (
          <Row spacingV="ll">
            <Column span={12}>
              <ErrorBox text={errors.map((error) => error.message)} />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <InformationBox variant="info" corners={corners} title="Adding a new transaction">
              <p className="body1">
                Record a quote, reservation or sale against this issuance. If this transaction completes or cancels an
                existing quote, reservation or sale, progress it from that transaction in the table instead, so the
                inventory stays accurate.
              </p>
            </InformationBox>
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TransactionCompanyPicker
              label="Counterparty"
              show={show}
              selectedUuid={issuanceTransactionPanelData.counterPartyCollaboratorUuid}
              selectedName={issuanceTransactionPanelData.counterPartyName}
              collaboratorTypes={CollaboratorTypeConstants.COUNTERPARTY_TYPES}
              variant={variant}
              onChange={(company) =>
                setIssuanceTransactionPanelData((prev) => ({
                  ...prev,
                  counterPartyCollaboratorUuid: company?.uuid ?? null,
                  counterPartyName: company?.name ?? null,
                }))
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TransactionCompanyPicker
              label="Broker"
              show={show}
              selectedUuid={issuanceTransactionPanelData.brokerCollaboratorUuid}
              selectedName={issuanceTransactionPanelData.brokerName}
              collaboratorTypes={CollaboratorTypeConstants.BROKER_TYPES}
              variant={variant}
              onChange={(company) =>
                setIssuanceTransactionPanelData((prev) => ({
                  ...prev,
                  brokerCollaboratorUuid: company?.uuid ?? null,
                  brokerName: company?.name ?? null,
                }))
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <DatePicker
              label="Date"
              value={issuanceTransactionPanelData.transactionDate ?? undefined}
              onChange={(value) =>
                setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, transactionDate: value ?? null })
              }
              corners={corners}
              font={font}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <Select
              label="Type"
              corners={corners}
              data={ProjectIssuanceTransactionConstants.ADD_ISSUANCE_TRANSACTION_TYPE_SELECT_DATA}
              value={issuanceTransactionPanelData.type}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  type: value,
                  // BR-02843 — only a quote carries an expiry, so changing away from Quote drops it.
                  expiryDate:
                    value === ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE
                      ? issuanceTransactionPanelData.expiryDate
                      : null,
                })
              }
            />
          </Column>
        </Row>
        {issuanceTransactionPanelData.type === ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE && (
          <Row spacingV="ll">
            <Column span={12}>
              <QuoteExpiryField
                transactionType={issuanceTransactionPanelData.type}
                value={issuanceTransactionPanelData.expiryDate ?? undefined}
                variant={variant}
                onChange={(date) =>
                  setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, expiryDate: date ?? null })
                }
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Quantity"
              name="Quantity"
              type="number"
              value={issuanceTransactionPanelData.quantity?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  quantity: value === "" ? null : parseFloat(Number(value).toFixed(5)),
                  totalValue:
                    issuanceTransactionPanelData.price && value !== ""
                      ? parseFloat(Number(value).toFixed(5)) * issuanceTransactionPanelData.price -
                        (issuanceTransactionPanelData.fees ?? 0)
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={999999.99}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Price"
              name="Price"
              type="number"
              prefix="£"
              value={issuanceTransactionPanelData.price?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  price: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                  totalValue:
                    issuanceTransactionPanelData.quantity && value !== ""
                      ? parseFloat(Number(value).toFixed(4)) * issuanceTransactionPanelData.quantity -
                        (issuanceTransactionPanelData.fees ?? 0)
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Fees"
              name="Fees"
              type="number"
              prefix="£"
              value={issuanceTransactionPanelData.fees?.toString()}
              onChange={(value) =>
                setIssuanceTransactionPanelData({
                  ...issuanceTransactionPanelData,
                  fees: value === "" ? null : parseFloat(Number(value).toFixed(4)),
                  totalValue:
                    issuanceTransactionPanelData.price && issuanceTransactionPanelData.quantity
                      ? issuanceTransactionPanelData.price * issuanceTransactionPanelData.quantity -
                        (value === "" ? 0 : parseFloat(Number(value).toFixed(4)))
                      : null,
                })
              }
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <ReadonlyText
              label="Total value"
              value={
                issuanceTransactionPanelData.totalValue !== null
                  ? `£${formatNumber(issuanceTransactionPanelData.totalValue)}`
                  : undefined
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TextInput
              label="Transaction reference"
              name="Transaction reference"
              value={issuanceTransactionPanelData.externalReference}
              onChange={(value) =>
                setIssuanceTransactionPanelData({ ...issuanceTransactionPanelData, externalReference: value })
              }
            />
          </Column>
        </Row>
        {issuance?.unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY && (
          <Row spacingV="ll">
            <Column span={12}>
              <TextInput
                label={`Unit size (${isUnitKmHabitatType(issuance?.habitatType ?? null) ? "Km" : "Ha"})`}
                name="Unit size Ha / Km"
                type="number"
                value={issuanceTransactionPanelData.areaHa?.toString()}
                onChange={(value) =>
                  setIssuanceTransactionPanelData({
                    ...issuanceTransactionPanelData,
                    areaHa: value === "" ? null : Number(value),
                  })
                }
                minNumberValue={0}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ll">
          <Column span={12}>
            <FormLabel label="Tags" />
            <PillsList pills={tagPills} linkText="+ Add a tag" linkOnClick={() => setShowTagsPanel(true)} />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <TransactionAttachments
              documents={documents}
              variant={variant}
              readOnly={!canManageAttachments}
              onAddAttachment={handleAddAttachment}
              onRemove={handleRemoveAttachment}
              onDownload={handleDownloadAttachment}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <ActionButtons
              submitButtonLabel="Save changes"
              corners={corners}
              onCancel={handleClosePanel}
              isLoading={isHandleSubmitLoading}
            />
          </Column>
        </Row>
      </form>
    );
  };

  return (
    <FlyoutPanel
      show={show}
      onClose={handleClosePanel}
      size="ExtraSmall"
      height="Full"
      className="TransactionPanel_flyout"
      header="Add transaction"
      stickyHeader={false}
      buttonsChildren={undefined}
      contentChildren={
        <>
          {content()}
          <TagsPanel
            objectUuid={undefined}
            objectType={InteractionObjectType.ISSUANCE_TRANSACTION}
            show={showTagsPanel}
            onClose={() => setShowTagsPanel(false)}
            shouldRefreshTags={() => setRefreshTags((prev) => !prev)}
            changedTagsExternal={changedTagsExternal}
            setChangedTagsExternal={setChangedTagsExternal}
          />
          {/* Uploads a project document, which handleDocumentsUploaded then links to the new transaction. */}
          {/*
            Portalled to the body because the flyout panel carries a transform, which makes it the containing
            block for position: fixed descendants. Left inside it, the modal centres on the narrow panel rather
            than the viewport and its right-hand side is cut off past the edge of the screen.
          */}
          {createPortal(
            <UploadDocumentModal
              show={showUploadDocumentModal}
              closeModal={handleCloseUploadDocumentModal}
              projectUuid={projectUuid}
              refreshDocuments={false}
              setRefreshDocuments={() => undefined}
              onUploaded={handleDocumentsUploaded}
              className="Modal_aboveFlyoutPanel"
            />,
            document.body
          )}
        </>
      }
    />
  );
};
